import React, { useState } from 'react';
import Button from './Button';
import Input from './Input';
import Icon from '../AppIcon';

const ScheduleConsultationModal = ({ isOpen, onClose, instructorName }) => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    topic: '',
    preferredDate: '',
    preferredTime: '',
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Prepare the email template data
      const emailData = {
        to_email: 'your_email@example.com', // Will be replaced with your email
        from_name: formData.fullName,
        from_email: formData.email,
        phone: formData.phone,
        instructor: instructorName,
        topic: formData.topic,
        preferred_date: formData.preferredDate,
        preferred_time: formData.preferredTime,
        message: formData.message,
      };

      // Initialize EmailJS (will be configured with your service ID and template ID)
      // This will be uncommented once you provide the EmailJS credentials
      /*
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        emailData,
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      );
      */

      // For now, just log the data
      console.log('Consultation scheduled:', emailData);

      setSubmitStatus({
        type: 'success',
        message: 'Consultation request submitted! We will get back to you soon.',
      });

      // Reset form
      setFormData({
        fullName: '',
        email: '',
        phone: '',
        topic: '',
        preferredDate: '',
        preferredTime: '',
        message: '',
      });

      // Close modal after 2 seconds
      setTimeout(() => {
        onClose();
        setSubmitStatus(null);
      }, 2000);
    } catch (error) {
      console.error('Error submitting consultation request:', error);
      setSubmitStatus({
        type: 'error',
        message: 'Failed to submit request. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-background rounded-lg border border-border w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 flex items-center justify-between p-6 border-b border-border bg-background z-10">
          <div className="flex items-center gap-3 flex-1">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <Icon name="Video" size={20} color="var(--color-primary)" />
            </div>
            <div className="min-w-0">
              <h2 className="text-xl font-bold text-foreground truncate">Schedule Consultation</h2>
              <p className="text-sm text-muted-foreground truncate">Book a 1-on-1 session with {instructorName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-smooth p-2 flex-shrink-0 hover:bg-muted rounded-md"
            type="button"
            aria-label="Close modal"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Status Messages */}
          {submitStatus && (
            <div
              className={`p-4 rounded-lg border ${
                submitStatus.type === 'success'
                  ? 'bg-green-50 border-green-200 text-green-800'
                  : 'bg-red-50 border-red-200 text-red-800'
              }`}
            >
              <p className="text-sm font-medium">{submitStatus.message}</p>
            </div>
          )}

          {/* Personal Information */}
          <div className="space-y-4">
            <h3 className="font-semibold text-foreground flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-xs font-bold text-primary">
                1
              </span>
              Personal Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                type="text"
                name="fullName"
                label="Full Name"
                placeholder="John Doe"
                value={formData.fullName}
                onChange={handleChange}
                required
              />
              <Input
                type="email"
                name="email"
                label="Email"
                placeholder="john@example.com"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>
            <Input
              type="tel"
              name="phone"
              label="Phone Number"
              placeholder="+1 (555) 123-4567"
              value={formData.phone}
              onChange={handleChange}
              required
            />
          </div>

          {/* Consultation Details */}
          <div className="space-y-4">
            <h3 className="font-semibold text-foreground flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-xs font-bold text-primary">
                2
              </span>
              Consultation Details
            </h3>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Topic of Interest
              </label>
              <select
                name="topic"
                value={formData.topic}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 transition-smooth"
              >
                <option value="">Select a topic...</option>
                <option value="course-guidance">Course Guidance</option>
                <option value="pronunciation">Pronunciation Improvement</option>
                <option value="conversation">Conversation Practice</option>
                <option value="hsk-exam">HSK Exam Preparation</option>
                <option value="business-chinese">Business Chinese</option>
                <option value="cultural-insights">Cultural Insights</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                type="date"
                name="preferredDate"
                label="Preferred Date"
                value={formData.preferredDate}
                onChange={handleChange}
                required
              />
              <Input
                type="time"
                name="preferredTime"
                label="Preferred Time"
                value={formData.preferredTime}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          {/* Message */}
          <div className="space-y-4">
            <h3 className="font-semibold text-foreground flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-xs font-bold text-primary">
                3
              </span>
              Additional Information
            </h3>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Message (Optional)
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Tell us more about what you'd like to discuss..."
                rows="4"
                className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 transition-smooth resize-none"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-border">
            <Button variant="outline" fullWidth onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              variant="default"
              fullWidth
              type="submit"
              disabled={isSubmitting}
              iconName={isSubmitting ? undefined : 'Send'}
              iconPosition="right"
            >
              {isSubmitting ? 'Scheduling...' : 'Schedule Consultation'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ScheduleConsultationModal;
